import { Loader } from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { type ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/auth';
import MainContent from './MainContent';
import styles from './MainLayout.css';
import PageTitle from './PageTitle';

interface MainLayoutProps {
  header?: React.ReactNode;
  // TODO: Consider moving away from the fixed-positioning layout model so that
  // elements can take up their natural space on the page. This would allow the
  // MainLayout component to not have to be aware if the nav is present.
  hasBottomNav?: boolean;
  shouldShowFooter?: boolean;
  children: React.ReactNode;
  useLegacyStyles?: boolean;
  promotionalBanner?: React.ReactNode;
}

const MainLayout = ({
  header,
  hasBottomNav,
  shouldShowFooter,
  children,
  useLegacyStyles = true,
  promotionalBanner,
  ...props
}: MainLayoutProps): ReactElement => {
  const { t } = useTranslation();
  const { loading } = useAuthContext();
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  let offsetClassName;
  if (!header) {
    offsetClassName = styles.noNavLayout;
  } else if (!hasBottomNav) {
    offsetClassName = styles.topNavOnlyLayout;
  } else {
    offsetClassName = styles.fullNavLayout;
  }

  return (
    <div className={styles.rootContent} {...props}>
      <div
        className={classNames(
          {
            [styles.legacyStyles]: useLegacyStyles
          },
          styles.wrap,
          offsetClassName
        )}
      >
        <PageTitle title="Axe DevTools" />
        {promotionalBanner || null}
        {header || null}
        {loading ? (
          <Loader
            className={styles.loader}
            label={t('Loading')}
            tabIndex={-1}
            ref={loaderRef}
          />
        ) : (
          <MainContent shouldShowFooter={shouldShowFooter}>
            {children}
          </MainContent>
        )}
      </div>
    </div>
  );
};

export default MainLayout;
